import { useEffect, useMemo, useState } from 'react';
import type { DataRetentionSettings, MfaRequiredTier, SecuritySettings } from '@orion/contracts';
import { Button, Menu, Switch, Textarea } from '@orion/ui';
import { Link } from '@tanstack/react-router';
import { PageHeader } from '../../../app/shell.js';
import { useAuditLogsQuery } from '../audit/api.js';
import { useActiveSessionCountQuery, useSettingsMetaQuery, useSettingsQuery, useSignOutAllSessions, useUpdateSettings } from './api.js';

const MFA_TIER_LABEL: Record<MfaRequiredTier, string> = { admins: 'Admins', managers: 'Managers', everyone: 'Everyone' };

// design/orion-crm-design/rendered-html/AdminSecurity.html freezes mid-edit
// (2 pending changes) — reproduced by replaying those exact 2 edits against
// the real dirty-tracking state, same approach as AdminRoles.
const isDesignSecurityPreview = import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design') === 'AdminSecurity';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function PresetMenu({ value, unit, options, onChange, disabled, dirty }: { value: number; unit: string; options: number[]; onChange: (v: number) => void; disabled?: boolean; dirty?: boolean }) {
  return (
    <Menu
      align="right"
      trigger={
        <button
          type="button"
          disabled={disabled}
          className={`flex h-8 w-35 items-center justify-between rounded-chip border px-2.5 text-sm font-medium ${
            dirty ? 'border-primary shadow-[0_0_0_3px_rgba(46,71,214,0.12)]' : 'border-border-strong'
          }`}
        >
          {value} {unit}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      }
      items={options.map((o) => ({ label: `${o} ${unit}`, onSelect: () => onChange(o) }))}
    />
  );
}

function EditedDot() {
  return <span role="img" aria-label="Edited" className="size-1.5 shrink-0 rounded-full bg-primary" />;
}

/** docs section 5 "Administrator Control" — the Security screen
 * (design/orion-crm-design/rendered-html/AdminSecurity.html). Bespoke
 * rather than the generic SettingsForm: the design's layout (two columns of
 * labelled cards, inline toggles/menus, a floating diff summary) doesn't
 * map onto a flat field list. */
export function SecuritySettingsPage() {
  const { data: security, isLoading: loadingSecurity } = useSettingsQuery<SecuritySettings>('security');
  const { data: retention, isLoading: loadingRetention } = useSettingsQuery<DataRetentionSettings>('data_retention');
  const { data: meta } = useSettingsMetaQuery('security');
  const { data: sessionCount } = useActiveSessionCountQuery();
  const { data: auditLogs } = useAuditLogsQuery({ page: 1 });
  const updateSecurity = useUpdateSettings<SecuritySettings>('security');
  const updateRetention = useUpdateSettings<DataRetentionSettings>('data_retention');
  const signOutAll = useSignOutAllSessions();

  const [draft, setDraft] = useState<SecuritySettings | null>(null);
  const [retentionDraft, setRetentionDraft] = useState<DataRetentionSettings | null>(null);
  const [ipAllowlistText, setIpAllowlistText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [signOutConfirm, setSignOutConfirm] = useState(false);

  useEffect(() => {
    if (security) {
      setDraft(isDesignSecurityPreview ? { ...security, passwordMinLength: 12, sessionIdleTimeoutMinutes: 30 } : security);
      setIpAllowlistText(security.ipAllowlist.join('\n'));
    }
  }, [security]);
  useEffect(() => {
    if (retention) setRetentionDraft(retention);
  }, [retention]);

  const changes = useMemo(() => {
    const list: Array<{ key: string; label: string; from: string; to: string }> = [];
    if (security && draft) {
      const fields: Array<[keyof SecuritySettings, string, (v: unknown) => string]> = [
        ['passwordMinLength', 'Minimum length', (v) => `${v}`],
        ['breachedPasswordCheck', 'Block breached passwords', (v) => (v ? 'On' : 'Off')],
        ['mfaRequiredFor', 'MFA required for', (v) => ((v as MfaRequiredTier[]).map((t) => MFA_TIER_LABEL[t]).join(', ') || 'No one')],
        ['sessionIdleTimeoutMinutes', 'Idle timeout', (v) => `${v} min`],
        ['sessionAbsoluteLifetimeHours', 'Absolute session lifetime', (v) => `${v}h`],
        ['rememberMeMaxDays', '"Remember me" maximum', (v) => `${v}d`],
        ['lockoutMaxAttempts', 'Lock account after', (v) => `${v} attempts`],
        ['lockoutWindowMinutes', 'Lockout duration', (v) => `${v} min`],
      ];
      for (const [key, label, fmt] of fields) {
        if (JSON.stringify(security[key]) !== JSON.stringify(draft[key])) {
          list.push({ key, label, from: fmt(security[key]), to: fmt(draft[key]) });
        }
      }
      const draftAllowlist = ipAllowlistText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      if (JSON.stringify(security.ipAllowlist) !== JSON.stringify(draftAllowlist)) {
        list.push({ key: 'ipAllowlist', label: 'IP allowlist', from: `${security.ipAllowlist.length} entries`, to: `${draftAllowlist.length} entries` });
      }
    }
    if (retention && retentionDraft) {
      if (retention.archivedRecordPurgeDays !== retentionDraft.archivedRecordPurgeDays) {
        list.push({ key: 'archivedRecordPurgeDays', label: 'Purge archived records after', from: `${retention.archivedRecordPurgeDays}d`, to: `${retentionDraft.archivedRecordPurgeDays}d` });
      }
      if (retention.auditLogRetentionDays !== retentionDraft.auditLogRetentionDays) {
        list.push({ key: 'auditLogRetentionDays', label: 'Keep audit logs for', from: `${retention.auditLogRetentionDays}d`, to: `${retentionDraft.auditLogRetentionDays}d` });
      }
    }
    return list;
  }, [security, draft, ipAllowlistText, retention, retentionDraft]);

  const changedKeys = new Set(changes.map((c) => c.key));
  const dirty = changes.length > 0;

  const set = <K extends keyof SecuritySettings>(key: K, value: SecuritySettings[K]) => {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  };
  const toggleTier = (tier: MfaRequiredTier) => {
    if (!draft) return;
    const has = draft.mfaRequiredFor.includes(tier);
    set('mfaRequiredFor', has ? draft.mfaRequiredFor.filter((t) => t !== tier) : [...draft.mfaRequiredFor, tier]);
  };

  const discard = () => {
    if (security) {
      setDraft(security);
      setIpAllowlistText(security.ipAllowlist.join('\n'));
    }
    if (retention) setRetentionDraft(retention);
  };

  const save = async () => {
    if (!draft || !retentionDraft) return;
    setError(null);
    try {
      await updateSecurity.mutateAsync({
        ...draft,
        ipAllowlist: ipAllowlistText
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      await updateRetention.mutateAsync(retentionDraft);
    } catch {
      setError('Some values were invalid — please check the fields below.');
    }
  };

  const securityEvents = (auditLogs?.data ?? [])
    .filter((e) => e.action.startsWith('auth.locked_out') || e.action.startsWith('settings.security') || e.action.startsWith('role.permissions') || e.action.startsWith('security.'))
    .slice(0, 2);

  const breadcrumb = [{ label: 'Administration', to: '/admin' }, { label: 'Security' }];

  if (loadingSecurity || loadingRetention || !draft || !retentionDraft) return <PageHeader breadcrumb={breadcrumb} />;

  return (
    <>
      <PageHeader breadcrumb={breadcrumb} />
      <main className="relative flex flex-col gap-4 overflow-hidden px-8 py-7" style={{ height: 'calc(100vh - 60px)', flex: 'none' }}>
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em]">Security</h1>
            <p className="text-sm leading-5 text-text-muted">Password rules, multi-factor authentication, sessions and sign-in protection for all users.</p>
          </div>
          <div className="flex items-center gap-3">
            {meta?.updatedAt && meta.updatedByName && (
              <span className="text-xs text-text-muted">
                Last changed {formatDate(meta.updatedAt)} by {meta.updatedByName}
              </span>
            )}
            <Link to="/admin/audit-log">
              <Button variant="secondary" size="md">
                View audit log
              </Button>
            </Link>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 items-start gap-3">
            <div className="flex flex-col gap-3">
              <section aria-labelledby="pw-h" className="rounded-dialog border border-border bg-surface shadow-card">
                <header className="flex h-12 items-center gap-2.5 border-b border-divider px-4.5">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="8" cy="15" r="4" />
                    <path d="m11 12 9-9M17 6l3 3" />
                  </svg>
                  <h2 id="pw-h" className="text-[15px] font-semibold">
                    Password policy
                  </h2>
                  <span className="text-xs text-text-muted">Email + password sign-in only</span>
                </header>
                <div className="flex flex-col px-4.5 pb-3.5">
                  <div className="flex h-10 items-center gap-3 border-b border-divider">
                    {changedKeys.has('passwordMinLength') && <EditedDot />}
                    <label htmlFor="pw-min" className={`flex-1 text-sm font-medium ${changedKeys.has('passwordMinLength') ? '-ml-1' : ''}`}>
                      Minimum length
                    </label>
                    <input
                      id="pw-min"
                      type="number"
                      value={draft.passwordMinLength}
                      onChange={(e) => set('passwordMinLength', Number(e.target.value))}
                      className={`h-8 w-16 rounded-chip border px-2.5 text-sm tabular-nums ${changedKeys.has('passwordMinLength') ? 'border-primary shadow-[0_0_0_3px_rgba(46,71,214,0.12)]' : 'border-border-strong'}`}
                    />
                    <span className="w-16 text-[13px] text-text-muted">characters</span>
                  </div>
                  <div className="flex h-10 items-center gap-3 border-b border-divider">
                    <Switch checked={draft.breachedPasswordCheck} onChange={(v) => set('breachedPasswordCheck', v)} label="Block breached passwords" hideLabel={false} />
                  </div>
                  <div className="flex h-10 items-center gap-3">
                    <span className="flex-1 text-sm font-medium text-border-strong">Force periodic password expiry</span>
                    <span className="text-xs text-text-muted">Off</span>
                    <Switch checked={false} onChange={() => {}} disabled label="Force periodic password expiry" hideLabel />
                  </div>
                  <div className="flex items-start gap-2 rounded-control bg-primary-soft p-2.5">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-soft-text)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-0.5 shrink-0">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 11v5M12 8h.01" />
                    </svg>
                    <span className="text-xs leading-4.25 text-primary-soft-text">
                      Off by design: NIST SP 800-63B advises against scheduled resets. Users change passwords only if a breach is suspected.
                    </span>
                  </div>
                </div>
              </section>

              <section aria-labelledby="mfa-h" className="rounded-dialog border border-border bg-surface shadow-card">
                <header className="flex h-12 items-center gap-2.5 border-b border-divider px-4.5">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                  <h2 id="mfa-h" className="text-[15px] font-semibold">
                    Multi-factor authentication
                  </h2>
                  <span className="flex-1" />
                  <span className="text-xs text-text-muted">SSO users use Entra MFA</span>
                </header>
                <div className="flex flex-col gap-2 px-4.5 pb-2.5">
                  <div role="group" aria-labelledby="mfa-req" className="flex h-11 items-center gap-4 border-b border-divider">
                    {changedKeys.has('mfaRequiredFor') && <EditedDot />}
                    <span id="mfa-req" className="w-27.5 text-sm font-medium">
                      Required for
                    </span>
                    {(['admins', 'managers', 'everyone'] as const).map((tier) => (
                      <label key={tier} className="flex items-center gap-1.5 text-sm">
                        <input type="checkbox" checked={draft.mfaRequiredFor.includes(tier)} onChange={() => toggleTier(tier)} className="size-3.75" style={{ accentColor: 'var(--color-primary)' }} />
                        {MFA_TIER_LABEL[tier]}
                      </label>
                    ))}
                  </div>
                  <div role="group" aria-labelledby="mfa-methods" className="flex gap-4 pt-2">
                    <span id="mfa-methods" className="w-27.5 shrink-0 pt-1.5 text-sm font-medium">
                      Methods
                    </span>
                    <div className="flex flex-col gap-1">
                      <label className="flex h-7.5 items-center gap-1.5 text-sm">
                        <input type="checkbox" checked disabled className="size-3.75" style={{ accentColor: 'var(--color-primary)' }} />
                        Authenticator app (TOTP)
                      </label>
                      <label className="flex h-7.5 items-center gap-1.5 text-sm text-border-strong">
                        <input type="checkbox" disabled className="size-3.75" />
                        WebAuthn / passkeys
                        <span className="flex h-5 items-center rounded-full bg-violet-soft px-1.75 text-[11px] font-semibold text-violet">Coming soon</span>
                      </label>
                    </div>
                  </div>
                </div>
              </section>

              <section aria-labelledby="ret-h" className="rounded-dialog border border-border bg-surface shadow-card">
                <header className="flex h-12 items-center gap-2.5 border-b border-divider px-4.5">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" />
                  </svg>
                  <h2 id="ret-h" className="text-[15px] font-semibold">
                    Data retention
                  </h2>
                  <span className="flex-1" />
                  <Link to="/admin/retention" className="flex items-center gap-1 text-[13px] font-semibold text-primary">
                    Data retention page
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                  </Link>
                </header>
                <div className="flex flex-col px-4.5 pb-1.5">
                  <div className="flex h-10 items-center gap-3 border-b border-divider">
                    <label htmlFor="ret-arch" className="flex-1 text-sm font-medium">
                      Purge archived records after
                    </label>
                    <input
                      id="ret-arch"
                      type="number"
                      value={retentionDraft.archivedRecordPurgeDays}
                      onChange={(e) => setRetentionDraft((d) => (d ? { ...d, archivedRecordPurgeDays: Number(e.target.value) } : d))}
                      className="h-8 w-16 rounded-chip border border-border-strong px-2.5 text-sm tabular-nums"
                    />
                    <span className="w-16 text-[13px] text-text-muted">days</span>
                  </div>
                  <div className="flex h-10 items-center gap-3">
                    <label htmlFor="ret-audit" className="flex-1 text-sm font-medium">
                      Keep audit logs for
                    </label>
                    <input
                      id="ret-audit"
                      type="number"
                      value={retentionDraft.auditLogRetentionDays}
                      onChange={(e) => setRetentionDraft((d) => (d ? { ...d, auditLogRetentionDays: Number(e.target.value) } : d))}
                      className="h-8 w-16 rounded-chip border border-border-strong px-2.5 text-sm tabular-nums"
                    />
                    <span className="w-16 text-[13px] text-text-muted">days</span>
                  </div>
                </div>
              </section>
            </div>

            <div className="flex flex-col gap-3">
              <section aria-labelledby="ses-h" className="rounded-dialog border border-border bg-surface shadow-card">
                <header className="flex h-12 items-center gap-2.5 border-b border-divider px-4.5">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="12" rx="2" />
                    <path d="M8 20h8M12 16v4" />
                  </svg>
                  <h2 id="ses-h" className="text-[15px] font-semibold">
                    Sessions
                  </h2>
                  <span className="flex-1" />
                  <span className="text-xs text-text-muted">{sessionCount?.count ?? '—'} active sessions</span>
                </header>
                <div className="flex flex-col px-4.5 pb-3.5">
                  <div className="flex h-11 items-center gap-3 border-b border-divider">
                    {changedKeys.has('sessionIdleTimeoutMinutes') && <EditedDot />}
                    <span id="ses-idle" className="flex-1 text-sm font-medium">
                      Idle timeout
                    </span>
                    <PresetMenu
                      value={draft.sessionIdleTimeoutMinutes}
                      unit="minutes"
                      options={[15, 30, 60, 120]}
                      onChange={(v) => set('sessionIdleTimeoutMinutes', v)}
                      dirty={changedKeys.has('sessionIdleTimeoutMinutes')}
                    />
                  </div>
                  <div className="flex h-11 items-center gap-3 border-b border-divider">
                    <span id="ses-abs" className="flex-1 text-sm font-medium">
                      Absolute session lifetime
                    </span>
                    <PresetMenu
                      value={draft.sessionAbsoluteLifetimeHours}
                      unit="hours"
                      options={[1, 4, 8, 12, 24, 72]}
                      onChange={(v) => set('sessionAbsoluteLifetimeHours', v)}
                      dirty={changedKeys.has('sessionAbsoluteLifetimeHours')}
                    />
                  </div>
                  <div className="flex h-11 items-center gap-3">
                    <span id="ses-rem" className="flex-1 text-sm font-medium">
                      &ldquo;Remember me&rdquo; maximum
                    </span>
                    <PresetMenu
                      value={draft.rememberMeMaxDays}
                      unit="days"
                      options={[7, 14, 30, 60, 90]}
                      onChange={(v) => set('rememberMeMaxDays', v)}
                      dirty={changedKeys.has('rememberMeMaxDays')}
                    />
                  </div>
                  <div className="mt-3 flex items-center gap-3 rounded-control border border-[#f3c9c4] bg-[#fffbfa] p-2.5">
                    <div className="flex flex-1 flex-col gap-0.5">
                      <span className="text-sm font-semibold text-danger">Sign out all users</span>
                      <span className="text-xs text-text-muted">Ends all {sessionCount?.count ?? 0} sessions now, including yours.</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSignOutConfirm(true)}
                      className="flex h-8 items-center rounded-chip border border-danger bg-surface px-3 text-[13px] font-semibold text-danger"
                    >
                      Sign out all users
                    </button>
                  </div>
                  {signOutConfirm && (
                    <div className="mt-2 flex items-center justify-end gap-2 text-xs">
                      <span className="mr-auto text-text-muted">Are you sure? This can&rsquo;t be undone.</span>
                      <Button variant="secondary" size="sm" onClick={() => setSignOutConfirm(false)}>
                        Cancel
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        loading={signOutAll.isPending}
                        onClick={async () => {
                          await signOutAll.mutateAsync();
                          setSignOutConfirm(false);
                        }}
                      >
                        Confirm
                      </Button>
                    </div>
                  )}
                </div>
              </section>

              <section aria-labelledby="lp-h" className="rounded-dialog border border-border bg-surface shadow-card">
                <header className="flex h-12 items-center gap-2.5 border-b border-divider px-4.5">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="4" y="11" width="16" height="10" rx="2" />
                    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                  </svg>
                  <h2 id="lp-h" className="text-[15px] font-semibold">
                    Login protection
                  </h2>
                </header>
                <div className="flex flex-col px-4.5 pb-3.5">
                  <div className="flex h-11 items-center gap-2 border-b border-divider text-sm">
                    {(changedKeys.has('lockoutMaxAttempts') || changedKeys.has('lockoutWindowMinutes')) && <EditedDot />}
                    <span className="flex-1 font-medium">Lock account after</span>
                    <input
                      type="number"
                      aria-label="Failed attempts before lockout"
                      value={draft.lockoutMaxAttempts}
                      onChange={(e) => set('lockoutMaxAttempts', Number(e.target.value))}
                      className="h-8 w-13 rounded-chip border border-border-strong px-2.5 text-sm"
                    />
                    <span className="text-[13px] text-text-muted">failed attempts for</span>
                    <input
                      type="number"
                      aria-label="Lockout duration in minutes"
                      value={draft.lockoutWindowMinutes}
                      onChange={(e) => set('lockoutWindowMinutes', Number(e.target.value))}
                      className="h-8 w-13 rounded-chip border border-border-strong px-2.5 text-sm"
                    />
                    <span className="text-[13px] text-text-muted">min</span>
                  </div>
                  <div className="flex h-11 items-center gap-3 border-b border-divider">
                    <span id="lp-rate" className="flex-1 text-sm font-medium">
                      Rate-limit sign-in attempts per IP
                    </span>
                    <Switch checked disabled onChange={() => {}} label="Rate-limit sign-in attempts per IP" hideLabel />
                  </div>
                  <label className="flex flex-col gap-1.5 pt-2.5 text-sm font-medium">
                    <span className="flex items-center gap-2">
                      IP allowlist <span className="text-xs font-medium text-text-muted">Optional</span>
                      {changedKeys.has('ipAllowlist') && <EditedDot />}
                    </span>
                    <Textarea
                      label="IP allowlist"
                      hideLabel
                      rows={2}
                      value={ipAllowlistText}
                      onChange={(e) => setIpAllowlistText(e.target.value)}
                      placeholder="203.0.113.0/24 — one IP or CIDR range per line"
                      className="font-mono text-[13px]"
                    />
                    <span className="text-xs font-normal text-text-muted">
                      {ipAllowlistText.trim() ? 'Sign-in is restricted to these networks.' : 'Empty — sign-in is allowed from any network.'}
                    </span>
                  </label>
                </div>
              </section>

              <section aria-labelledby="ev-h" className="flex flex-col gap-2 rounded-dialog border border-border bg-surface px-4.5 py-3 shadow-card">
                <div className="flex items-center justify-between">
                  <h2 id="ev-h" className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                    Recent security events
                  </h2>
                  <Link to="/admin/audit-log" className="text-xs font-semibold text-primary">
                    View all
                  </Link>
                </div>
                {securityEvents.length === 0 ? (
                  <p className="text-[13px] text-text-muted">No security events yet.</p>
                ) : (
                  securityEvents.map((e) => (
                    <div key={e.id} className="flex items-center gap-2 text-[13px]">
                      <span className="size-1.5 shrink-0 rounded-full" style={{ background: e.action.includes('locked_out') ? 'var(--color-amber-deep)' : 'var(--color-success)' }} />
                      <span className="flex-1 truncate text-text-secondary">{e.action.replace(/[._]/g, ' ')}</span>
                      <span className="text-xs text-text-muted">{formatDate(e.occurredAt)}</span>
                    </div>
                  ))
                )}
              </section>
            </div>
          </div>
        </div>

        {error && (
          <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        {dirty && (
          <div role="region" aria-label="Unsaved changes" className="absolute inset-x-8 bottom-5 flex h-14 items-center gap-3 rounded-dialog bg-night pl-4.5 pr-2.5 text-white shadow-dialog">
            <span className="size-2 shrink-0 rounded-full" style={{ background: 'var(--color-amber-deep)' }} />
            <span className="text-sm font-semibold">You have unsaved changes</span>
            <span className="truncate text-[13px] text-[#9aa0ab]">
              {changes.length} setting{changes.length === 1 ? '' : 's'} · {changes.map((c) => `${c.label} ${c.from} → ${c.to}`).slice(0, 2).join(' · ')}
            </span>
            <span className="flex-1" />
            <button
              type="button"
              onClick={discard}
              className="flex h-9 items-center rounded-control border border-[#3a404c] px-3.5 text-sm font-semibold text-[#e6e8ec] hover:bg-white/5"
            >
              Discard
            </button>
            <Button size="md" onClick={save} loading={updateSecurity.isPending || updateRetention.isPending}>
              Save changes
            </Button>
          </div>
        )}
      </main>
    </>
  );
}
